import { AuthenticatedRoute, AuthViewProps, GoogleCallback, UnauthenticatedRoute } from "@homebound/auth-components";
import { Navigate, Route, Routes as RouterRoutes } from "react-router-dom";
import { BusinesFunctionDetailPage } from "src/components/change-log/BusinessFunctionDetailsPage";
import { BusinessFunctionsPage } from "src/components/change-log/BusinessFunctionsPage";
import { DevelopmentScheduleCostMappingPage } from "src/routes/developments/cost-mapping/DevelopmentScheduleCostMappingPage";
import { PersonalDashboardPage } from "src/routes/personal-dashboard/PersonalDashboard";
import { ProjectLayout } from "src/routes/projects/ProjectLayout";
import { ProjectsDevelopmentsPage } from "src/routes/projects/ProjectsDevelopmentsPage";
import { ChangeTypesPage } from "./components/change-log/ChangeTypesPage";
import { Admin } from "./routes/admin/Admin";
import { SignIn } from "./routes/auth/SignIn";
import { AddBidPackagePage } from "./routes/bids/add-bid-package/AddBidPackagePage";
import { BidPackageDetailPage } from "./routes/bids/BidPackageDetailPage";
import { BidPackagesPage } from "./routes/bids/BidPackagesPage";
import { EnterPlanBasedBidPage } from "./routes/bids/plan-based-bid-package/EnterPlanBasedBidPage";
import { PlanBasedBidPackagePage } from "./routes/bids/plan-based-bid-package/PlanBasedBidPackagePage";
import { FinancesPage } from "./routes/billing/FinancesPage";
import { InvoiceTermsForm } from "./routes/billing/invoice-terms/components/InvoiceTermsForm";
import { BillEditor } from "./routes/bills/BillEditor";
import { BillPage } from "./routes/bills/BillPage";
import { ChangeLogPage } from "./routes/change-log/ChangeLogPage";
import { DevelopmentCommitmentPage } from "./routes/development-commitments/DevelopmentCommitmentPage";
import { DevelopmentCommitmentsPage } from "./routes/development-commitments/DevelopmentCommitmentsPage";
import { DevelopmentLayout } from "./routes/developments/DevelopmentLayout";
import { ReleaseToTradesPage } from "./routes/developments/lot-summary/lot-release/ReleaseToTradesPage";
import { EditOptionsPage } from "./routes/developments/plan-and-options/options/EditOptionsPage";
import { ReassignPurchaseOrdersPage } from "./routes/developments/reassign-purchase-orders/ReassignPurchaseOrdersPage";
import { ReviewAndPublishPage } from "./routes/developments/templates/review-and-publish/ReviewAndPublishPage";
import { ExpensesAdmin } from "./routes/expenses/ExpensesAdmin";
import { InboxPage } from "./routes/inbox/InboxPage";
import { TradesLayout } from "./routes/libraries/global-options/TradesLayout";
import { LibrariesLayout } from "./routes/libraries/LibrariesLayout";
import { NotFound } from "./routes/NotFound";
import { UserProfile } from "./routes/profile/UserProfile";
import {
  adminPaths,
  bidPackageDetailsPath,
  bidPackagesEditPath,
  bidPackagesPath,
  billDetailsPath,
  billsPath,
  changeLogPaths,
  costMappingPath,
  createBidPackagePath,
  createBillPath,
  createPlanBasedBidPackagePath,
  developmentCommitmentsPath,
  developmentCommitmentsPaths,
  developmentPaths,
  developmentsPath,
  enterPlanBasedBidPath,
  expensesPath,
  inboxPaths,
  invoicesPath,
  invoiceTermPath,
  invoiceTermsPath,
  librariesPath,
  lienWaiversPath,
  paymentTermsPath,
  personalDashboardPaths,
  planBasedBidPackagesEditPath,
  projectPaths,
  projectsPath,
  purchaseOrdersPath,
  reassignPurchaseOrdersPath,
  searchPath,
  tradeCategoryLeadTimesPath,
  tradePartnerPaths,
  tradePartnersPath,
  tradesPath,
  warrantyPaths,
} from "./routes/routesDef";
import { SearchPage } from "./routes/search/Search";
import { TradeCategories } from "./routes/trade-categories/TradeCategories";
import { PaymentTermsPage } from "./routes/trade-partners/payment-terms/PaymentTermsPage";
import { TradeCategoryLeadTimesPage } from "./routes/trade-partners/trade-category-lead-times/TradeCategoryLeadTimesPage";
import { TradePartnerPage } from "./routes/trade-partners/TradePartnerPage";
import { TradePartnersPage } from "./routes/trade-partners/TradePartnersPage";
import { WarrantyPage } from "./routes/warranty/WarrantyPage";
import { WarrantyTicketPage } from "./routes/warranty/WarrantyTicketPage";

export type ProjectRouteProps = { projectId: string };

export function Routes({ authProps }: { authProps: AuthViewProps }) {
  return (
    <RouterRoutes>
      <Route path="/" element={<Navigate to={personalDashboardPaths.base} replace />} />

      <Route element={<UnauthenticatedRoute authProps={authProps} />}>
        <Route path="/signIn" element={<SignIn {...authProps} />} />
        <Route path="/auth/gcallback" element={<GoogleCallback />} />
      </Route>

      <Route element={<AuthenticatedRoute authProps={authProps} />}>
        <Route path="/profile" element={<UserProfile {...authProps} />} />
        <Route path={`${projectsPath}`} element={<ProjectsDevelopmentsPage />} />
        <Route path={`${developmentsPath}`} element={<ProjectsDevelopmentsPage />} />
        <Route path={`${projectPaths.releaseToTrades}/*`} element={<ReleaseToTradesPage />} />
        <Route path={`${reassignPurchaseOrdersPath}/*`} element={<ReassignPurchaseOrdersPage />} />
        <Route path={developmentPaths.reviewTemplates} element={<ReviewAndPublishPage />} />
        <Route path={developmentPaths.editOptions} element={<EditOptionsPage />} />
        <Route path={`${developmentPaths.development}/*`} element={<DevelopmentLayout />} />
        <Route path={costMappingPath} element={<DevelopmentScheduleCostMappingPage />} />
        <Route path={`${projectPaths.project}/*`} element={<ProjectLayout />} />
        <Route path={`${inboxPaths.base}/*`} element={<InboxPage />} />
        <Route path={personalDashboardPaths.base} element={<PersonalDashboardPage />} />
        <Route path={`${personalDashboardPaths.base}/:section`} element={<PersonalDashboardPage />} />
        {/* Support old `/my-blueprint` redirects for historical purposes */}
        <Route path="/my-blueprint" element={<Navigate to={personalDashboardPaths.actions} replace />} />
        <Route path="/my-blueprint/to-dos" element={<Navigate to={personalDashboardPaths.toDos} replace />} />
        <Route path="/my-blueprint/activity-feed" element={<Navigate to={personalDashboardPaths.activity} replace />} />
        <Route path={`${billsPath}/*`} element={<FinancesPage />} />
        <Route path={`${invoicesPath}/*`} element={<FinancesPage />} />
        <Route path={`${purchaseOrdersPath}/*`} element={<FinancesPage />} />
        <Route path={`${invoiceTermsPath}/*`} element={<FinancesPage />} />
        <Route path={`${lienWaiversPath}/*`} element={<FinancesPage />} />
        <Route path={createBillPath} element={<BillEditor />} />
        <Route path={billDetailsPath} element={<BillPage />} />
        <Route path="/admin/tradeCategories" element={<TradeCategories />} />
        <Route path={`${invoiceTermPath}/*`} element={<InvoiceTermsForm />} />
        <Route path={`${adminPaths.admin}/*`} element={<Admin />} />
        <Route path={tradePartnersPath} element={<TradePartnersPage />} />
        <Route path={createPlanBasedBidPackagePath} element={<PlanBasedBidPackagePage />} />
        <Route path={planBasedBidPackagesEditPath} element={<PlanBasedBidPackagePage />} />
        <Route path={enterPlanBasedBidPath} element={<EnterPlanBasedBidPage />} />
        <Route path={createBidPackagePath} element={<AddBidPackagePage />} />
        <Route path={bidPackagesEditPath} element={<AddBidPackagePage />} />
        <Route path={bidPackagesPath} element={<BidPackagesPage />} />
        <Route path={bidPackageDetailsPath} element={<BidPackageDetailPage />} />
        <Route path={`${tradePartnerPaths.tradePartner}/*`} element={<TradePartnerPage />} />
        <Route path={`${paymentTermsPath}/*`} element={<PaymentTermsPage />} />
        <Route path={tradeCategoryLeadTimesPath} element={<TradeCategoryLeadTimesPage />} />
        <Route path={`${tradesPath}/*`} element={<TradesLayout />} />
        <Route path={`${librariesPath}/*`} element={<LibrariesLayout />} />
        <Route path={developmentCommitmentsPath} element={<DevelopmentCommitmentsPage />} />
        <Route path={developmentCommitmentsPaths.developmentCommitment} element={<DevelopmentCommitmentPage />} />
        <Route path={changeLogPaths.businessFunctionDetails} element={<BusinesFunctionDetailPage />} />
        <Route path={changeLogPaths.businessFunctions} element={<BusinessFunctionsPage />} />
        <Route path={changeLogPaths.changeTypes} element={<ChangeTypesPage />} />
        <Route path={`${changeLogPaths.base}/*`} element={<ChangeLogPage />} />
        <Route path={expensesPath} element={<ExpensesAdmin />} />
        <Route path={searchPath} element={<SearchPage />} />
        <Route path={warrantyPaths.base} element={<WarrantyPage />} />
        <Route path={`${warrantyPaths.details}/*`} element={<WarrantyTicketPage />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </RouterRoutes>
  );
}
